import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  LinearProgress,
  DialogActions,
  Button,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles(() => ({
  dialogTitle: {
    paddingBottom: 0,
  },
  importProgressContainer: {
    position: "relative",
    minWidth: 400,
    padding: 6,
    boxSizing: "border-box",
  },
  importProgressBar: {
    height: 32,
    borderRadius: 4,
    width: "100%",
  },
  importProgressText: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#FFF",
    fontWeight: 700,
    fontSize: 14,
    pointerEvents: "none",
    textShadow: "0 1px 2px rgba(0,0,0,0.6)",
  },
}));

function ImportProgressDialog({
  open,
  processingRotation,
  activeFileName,
  importProgress,
  onClose,
  onAbort,
  abortLabel,
}) {
  const classes = useStyles();

  /**
   * Handles the abort action for the import process.
   * If an `onAbort` callback is provided, it will be called with the event.
   * Otherwise, if an `onClose` callback is provided, it will be called with the event.
   *
   * @param {React.SyntheticEvent} e - The event object triggered by the abort action.
   */
  const handleAbort = (e) => {
    if (onAbort) {
      onAbort(e);
      return;
    }
    if (onClose) onClose(e);
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={(e) => {
        if (onClose) onClose(e);
      }}
      style={{ pointerEvents: "auto" }}
    >
      <DialogTitle className={classes.dialogTitle}>
        {processingRotation ? "Rotating" : "Importing"} File: <br />
        {activeFileName}
      </DialogTitle>
      <div className={classes.importProgressContainer}>
        <LinearProgress
          variant="determinate"
          value={importProgress}
          className={classes.importProgressBar}
        />
        <div className={classes.importProgressText}>
          {(() => {
            const raw = Number(importProgress || 0);
            return raw + "%";
          })()}
        </div>
      </div>
      <DialogActions>
        <Button
          onClick={handleAbort}
          color="error"
          aria-label={
            abortLabel || processingRotation ? "Stop import" : "Abort rotation"
          }
        >
          {abortLabel ||
            (processingRotation ? "Stop import" : "Abort rotation")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ImportProgressDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  processingRotation: PropTypes.bool,
  activeFileName: PropTypes.string,
  importProgress: PropTypes.number,
  onClose: PropTypes.func,
  onAbort: PropTypes.func,
  abortLabel: PropTypes.string,
};

ImportProgressDialog.defaultProps = {
  processingRotation: false,
  activeFileName: "",
  importProgress: 0,
  onClose: null,
  onAbort: null,
  abortLabel: "Stop import",
};

export default ImportProgressDialog;
